import React, { useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * A small fixed button that hides the whole interface so the theme can be
 * looked at on its own, and brings it back. While hidden, Escape brings it
 * back too, so nobody is stranded with the controls gone.
 *
 * The button itself is themed through the same orange utility classes the
 * rest of the interface uses, so each theme's palette remaps it; it sits
 * above the interface and below the jump scare.
 */
const HideInterfaceToggle = ({ hidden, onToggle }) => {
  useEffect(() => {
    if (!hidden) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onToggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hidden, onToggle]);

  const label = hidden ? 'Show interface' : 'Hide interface';
  return (
    <button
      type="button"
      data-hide-interface
      data-hidden={hidden ? 1 : 0}
      onClick={onToggle}
      aria-pressed={hidden}
      aria-label={label}
      title={hidden ? `${label} (Esc)` : label}
      // z-[80]: above the interface, the countdown, and the haunted house's
      // secret door when it comes forward (75) - on a phone the door opens
      // under this corner, and the way back must never be covered. Below
      // the jump scare (9999).
      className="app-hide-toggle fixed bottom-4 right-4 z-[80] inline-flex items-center gap-2 rounded-full border border-orange-600 bg-gray-900/80 px-3 py-2 text-sm font-semibold text-orange-200 shadow-lg backdrop-blur-sm hover:bg-orange-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-400"
    >
      {hidden ? <Eye size={16} aria-hidden="true" /> : <EyeOff size={16} aria-hidden="true" />}
      <span>{label}</span>
    </button>
  );
};

export default HideInterfaceToggle;
